import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import ContactSection from "./ContactSection";
import ScrollToNextSectionButton from "./ScrollToNextSectionButton";
import HtmlRenderer from "./HtmlRenderer";
import glv from "../utils/getLocalizedValue"
import { Popup } from "./popup/Popup";
import PopupViewer from "./popup/PopupViewer";
import HomeLiquidIQSection from "./HomeLiquidIQSection";

const publicUrl = process.env.PUBLIC_URL || "";

function RotatingItems({ activeIndex, messages }) {
  return (
    <div className="relative">
      {messages.map((message, index) => (
        <span
          key={index}
          className={`absolute transition-opacity duration-1000 ${
            index === activeIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          {message}
        </span>
      ))}
    </div>
  );
}
function RotatingImages({ activeIndex, locations }) {
  return (
    <>
      {locations &&
        locations.map((location, index) => (
          <img
            key={index}
            src={location}
            alt=""
            className={`absolute transition-opacity duration-1000 w-full h-full object-cover ${
              index === activeIndex ? "opacity-40" : "opacity-0"
            }`}
          />
        ))}
    </>
  );
}

function Content({title="",imgSrc="",imgClass="", content="", linkText="", link="", img}) {
  return (
    <>
      <div className="font-pretendard flex relative w-full h-full justify-center lg:justify-end items-center">
        <div className="absolute w-full h-full top-0 left-0 -z-50 bg-brand-silicon-white flex justify-center items-center">
          {!img && imgSrc !== "" && (
            <img
              src={imgSrc}
              alt=""
              className={
                imgClass !== ""
                  ? imgClass
                  : "absolute transition-opacity duration-1000 w-full h-full object-cover"
              }
            />
          )}
          {
            img&&img
          }
        </div>
        <div className="rounded-sm font-pretendard bg-brand-silicon-white max-w-[36rem] md:mx-0 mx-3 md:px-3 px-5 md:py-4 py-5 bg-opacity-[0.9]">
          <div className="font-black f-28 md:f-28 md:mb-10 mb-5">
            <h2>{<HtmlRenderer htmlString={title} />}</h2>
          </div>
          <div className="main-content overflow-wrap ">
            <span
              className="font-[300] text-[#5F5D59] tracking-[-0.015em] md:f-20 f-10"
            >
              {<HtmlRenderer htmlString={content} />}
            </span>
          </div>
          <div className="mt-10 font-extrabold">
            <Link to={link}>
              <span className="underline">{linkText}</span> ➜
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function Cover() {
  const [messages] = useState([
    "CPUs.",
    "GPUs.",
    "Servers.",
    "Datacenters.",
    "Automotives.",
  ]);
  const [rotateImages] = useState([
    require("../assets/cover/cpu.jpg"),
    require("../assets/cover/gpu.jpg"),
    require("../assets/cover/server.jpg"),
    require("../assets/cover/datacenter.jpg"),
    require("../assets/cover/automotive.jpg"),
  ]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return; 

    const interval = setTimeout(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 1500);

    return () => clearTimeout(interval);
  }, [activeIndex, isPaused, messages]);

  const handleNext = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % messages.length);
    resetPause();
  };

  const handlePrevious = () => {
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? messages.length - 1 : prevIndex - 1
    );
    resetPause();
  };

  const resetPause = () => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 2000);
  };

  return (
    <>
      <div className="flex relative w-full h-full items-center justify-center md:ps-[50vw] md:p-0 md:pe-[5rem] px-5">
        <button
          className="absolute flex px-2 h-full items-center justify-center left-0 top-0 z-50"
          onClick={handlePrevious}
        >
          <img
            src={require("../assets/icon/left-arrow.png")}
            alt=""
            className="w-[1rem] sm:w-[1.5rem] object-contain"
          />
        </button>
        <button
          className="absolute flex px-2 h-full items-center justify-center right-0 top-0 z-50"
          onClick={handleNext}
        >
          <img
            src={require("../assets/icon/right-arrow.png")}
            alt=""
            className="w-[1rem] sm:w-[1.5rem] object-contain"
          />
        </button>
        <div className="absolute w-full h-full top-0 left-0 bg-black overflow-hidden -z-50">
          <RotatingImages activeIndex={activeIndex} locations={rotateImages} />
        </div>
        <div className="flex flex-col text-white font-pretendard sm:text-4xl text-2xl sm:items-end items-center">
          <img
            src={require("../assets/koolmicro2.png")}
            alt=""
            className="sm:max-w-[30rem] max-w-[25rem] sm:px-0 px-8 w-full"
          />
          <div className="flex flex-col justify-end">
            <div className="w-full mt-10 mb-2">Liquid Cooling Solutions</div>
            <div className="flex w-full">
              <div className="flex text-white me-5">for</div>
              <div className="flex bg-brand-electric-blue rounded-md font-black text-black flex-grow">
                <RotatingItems activeIndex={activeIndex} messages={messages} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MainPage() {
  let ref = useRef();

  useEffect(()=>{
    if(ref.current) {
      const hash = window.location.hash;
      if (hash) {
        const targetElement = hash
          ? ref.current.querySelector(`[name="${hash.slice(1)}"]`)
          : null;
        if (targetElement) {
          targetElement.scrollIntoView();
        }
      }
    }

    const mainPageRef = ref.current;
    const sections = mainPageRef.querySelectorAll("section");
    const observer = new IntersectionObserver(
      (entries) => {
        const activeSection = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const sectionName = activeSection?.target.getAttribute("name");

        if (sectionName && window.location.hash !== `#${sectionName}`) {
          window.history.replaceState(null, "", `#${sectionName}`);
        }
      },
      {
        root: mainPageRef,
        threshold: [0.5, 0.65, 0.8],
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  },[])

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div
        ref={ref}
        className="flex-grow overflow-y-scroll font-pretendard"
        style={{
          scrollSnapType: "y proximity",
          scrollBehavior: "smooth",
          overscrollBehaviorY: "contain",
        }}
      >
        <PopupViewer />
        <ScrollToNextSectionButton container={ref.current} />
        <section
          name="1"
          className="w-full h-full"
          style={{ scrollSnapAlign: "start" }}
        >
          <Cover />
        </section>
        <section
          name="liquidiq"
          className="w-full h-full"
          style={{ scrollSnapAlign: "start" }}
          aria-labelledby="home-liquidiq-title"
        >
          <HomeLiquidIQSection scrollContainerRef={ref} />
        </section>
        <section
          name="2"
          className="w-full h-full relative bg-brand-silicon-white"
          style={{ scrollSnapAlign: "start" }}
        >
          <div
            className="flex flex-1 bg-cover bg-center"
          >
            <img
              src={`${publicUrl}/png/main/bg_cooler_smarter.png`}
              alt="background"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="w-full md:mt-[15svh] mt-[10svh] text-center font-pretendard relative">
              <div className="f-28 mb-2 font-bold">
                <h2>COOLER, SMARTER.</h2>
              </div>
              <div className="f-20 font-light">
                Patented Thermal Management Technology<br/>for the World’s Fastest Semiconductor Units.
              </div>
            </div>
          </div>
        </section>
        <section
          name="3"
          className="w-full h-full overflow-hidden"
          style={{ scrollSnapAlign: "start" }}
        >
          <Content
            title={glv(
              "A Liquid-Cooling Powerhouse",
              "마침내 완성된 강력한 수냉."
            )}
            img={
              <>
                <img
                  src={require("../assets/product/cpu-immc-angle.png")}
                  alt=""
                  className="absolute left-1/2 sm:left-[35%] -translate-x-1/2 h-[58%] sm:h-[82%] w-auto max-w-none object-contain"
                />
              </>
            }
            content={glv(
              "Now, how well you cool your semiconductors determines how well they perform. Lowering the temperature allows the semiconductor to work properly and even faster, without thermal throttling.<br/><br/>KoolMicro‘s team of experts with 25+ years of experience has therefore developed the Integrated Manifold MicroChannel (IMMC) technology to to cool high-power-density semiconductors up to eight times more effective than conventional cooling method.",
              `<span class="text-[#5F5D59]">
                반도체의 성능은 얼마나 효과적으로 냉각하느냐에 달려 있습니다.
                온도를 낮추면 반도체가 제 성능을 발휘할 수 있을 뿐 아니라 과열 방지 때문에 성능 저하를 할 필요없이 더욱 빠르게 동작할 수 있습니다.
                <br/><br/>
                KoolMicro 전문가 팀은 25년 이상의 전문성을 바탕으로 
                고전력 고집적 반도체를 기존 방식보다 최대 8배 이상 더 효율적으로 냉각할 수 있는 IMMC(Integrated Maniford MicroChannel) 기술을 개발, 상용화하는데 성공했습니다.
              </span>`
            )}
            linkText={glv(
              "More about the People behind KoolMicro",
              "KoolMicro 회사 소개 더보기"
            )}
            link="/company/about-us"
          />
        </section>
        <section
          name="4"
          className="w-full h-full relative bg-[#EDEDED]"
          style={{ scrollSnapAlign: "start" }}
        >
          <img
            src={`${publicUrl}/png/main/bg_patent_driven_technology.png`}
            alt="background"
            className="absolute inset-0 max-h-[90%] w-full object-cover object-top mt-auto"
          />
          <div className="px-5 mx-auto 3xl:max-w-4xl max-w-3xl bg-brand-silicon-white bg-opacity-[0.1] z-10 relative w-full md:pt-[8svh] pt-[15svh] text-center">
            <div className="font-extrabold f-28 md:f-28 mb-5">
              <h2>{glv("Patent-Driven Technology.", "글로벌 특허 기술.")}</h2>
            </div>
            <div className="font-[300] text-[#5F5D59]">
              {
                glv(
                  <p>KoolMicro’s IMMC technology powers advanced semiconductor cooling by optimizing internal structures for efficient heat transfer. Unlike traditional methods, it leverages innovative fluid dynamics for direct, superior thermal management.<br/><br/>With a Korean-granted patent and multiple U.S. applications in progress, our intellectual property portfolio safeguards the uniqueness and performance of our solutions on a global scale.</p>,
                  <p><span class="text-[#5F5D59]">IMMC 기술은 내부 구조를 최적화하여 효율적인 열 전달을 구현함으로써 첨단 반도체 냉각을 가능하게 합니다.<br/>기존 방식과 차별화된 혁신적인 방식으로 유체 역학을 활용해 직접적이고 우수한 열 관리 성능을 제공합니다.<br/><br/>국내에서 특허를 취득했으며, 다수의 미국 특허도 진행중인 상황으로 IMMC 기술의 독창성과 성능은 국내뿐 아니라 글로벌 차원에서 보호받고 있습니다.</span></p>
                )
              }
            </div>
            <div className="mt-10 font-semibold">
              <Link to={"/technology"}>
                {glv(<p><span className="underline">Learn more about our Tech</span> ➜</p>,
                <p><span className="underline">IMMC 기술 자세히 알아보기</span> ➜</p>)}
              </Link>
            </div>
          </div>
        </section>
        <section
          name="5"
          className="w-full h-full relative bg-[#EDEDED] flex"
          style={{ scrollSnapAlign: "start" }}
        >
          <img
            src={`${publicUrl}/png/main/bg_anywhere_everywhere.png`}
            alt="background"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="p-5 text-white bg-black bg-opacity-[0.9] z-10 md:absolute md:top-20 md:left-20 relative md:mx-10 mx-5 max-w-lg my-auto">
            <div className="font-extrabold f-28 md:f-28 mb-5">
              <h2>{glv(
                  "Anywhere, Everywhere.",
                  "어느곳 어디든, 한계없는 냉각."
                )}</h2>
            </div>
            <div className="font-[300]">
              {
                glv(
                  <p>IMMC's unique manifold microchannel structure, chip integration method, and flow control method enables uniform cooling of a larger area with lower pumping power at a reasonable price.<br/><br/>We offer innovative Direct-to-Chip liquid cooling solutions for AI and high-performance semiconductor chips, optimizing performance and dramatically enhancing energy efficiency for data centers, semiconductor manufacturers, and the automotive industry as a global leader and trusted partner.</p>,
                  <p>
                    IMMC 기술의 독창적인 구조, 칩 집적 방식, 그리고 유동 제어 기술은 합리적인 비용과 낮은 펌핑 전력으로 더 넓은 영역을 균일하게 냉각할 수 있게 합니다.<br/><br/>
                    인공지능 및 고성능 반도체 칩을 위한 혁신적인 Direct-to-Chip 수냉 솔루션을 제공하며, 
                    성능을 최적화하고 에너지효율을 획기적으로 향상시킵니다.<br/><br/>
                    글로벌 리더이자 신뢰할 수 있는 파트너로서 데이터센터, 반도체 제조업, 자동차 산업 등 필요한 곳 어디든 고성능 반도체를 위한 맞춤형 냉각 솔루션을 제공합니다.
                  </p>
                )
              }
            </div>
            <div className="mt-10 font-semibold">
              <Link to={"/technology"}>
                {glv(<p><span className="underline">Learn more about our Tech</span> ➜</p>,
                <p><span className="underline">IMMC 기술 자세히 알아보기</span> ➜</p>)}
              </Link>
            </div>
          </div>
        </section>

        {/* {isMobile ? (
          <>
            <section
              name="3"
              className="w-full h-full overflow-hidden"
              style={{ scrollSnapAlign: "start" }}
            >
              <Content
                title={glv(
                  "Anywhere, Everywhere.",
                  "어느곳 어디든, 한계없는 냉각."
                )}
                img={
                  <>
                    <div className="flex justify-start items-center h-full w-full">
                      <div className="h-full sm:w-1/2 w-full grid grid-cols-1 gap-1">
                        <div className="w-full flex items-center justify-center overflow-y-hidden">
                          <img
                            src={require("../assets/cover/cpu.jpg")}
                            className="w-full object-cover"
                            alt=""
                          />
                        </div>
                        <div className="w-full flex items-center justify-center overflow-y-hidden">
                          <img
                            src={require("../assets/cover/gpu.jpg")}
                            className="w-full object-cover"
                            alt=""
                          />
                        </div>
                        <div className="w-full h-full flex items-center justify-center overflow-y-hidden">
                          <img
                            src={require("../assets/cover/server.jpg")}
                            className="w-full object-cover"
                            alt=""
                          />
                        </div>
                        <div className="w-full flex items-center justify-center overflow-y-hidden">
                          <img
                            src={require("../assets/cover/datacenter.jpg")}
                            className="w-full object-cover"
                            alt=""
                          />
                        </div>
                        <div className="w-full flex items-center justify-center overflow-y-hidden">
                          <img
                            src={require("../assets/cover/automotive.jpg")}
                            className="w-full object-cover"
                            alt=""
                          />
                        </div>
                      </div>
                    </div>
                  </>
                }
                content={glv(
                  "IMMC's unique manifold microchannel structure, chip integration method, and flow control method enables uniform cooling of a larger area with lower pumping power at a reasonable price.<br/><br/>We offer innovative Direct-to-Chip liquid cooling solutions for AI and high-performance semiconductor chips, optimizing performance and dramatically enhancing energy efficiency for data centers, semiconductor manufacturers, and the automotive industry as a global leader and trusted partner.",
                  `<span class='text-[#5F5D59]'>
                    IMMC 기술의 독창적인 구조, 칩 집적 방식, 그리고 유동 제어 기술은 합리적인 비용과 낮은 펌핑 전력으로 더 넓은 영역을 균일하게 냉각할 수 있게 합니다.
                    인공지능 및 고성능 반도체 칩을 위한 혁신적인 Direct-to-Chip 수냉 솔루션을 제공하며, 
                    성능을 최적화하고 에너지효율을 획기적으로 향상시킵니다. 
                    글로벌 리더이자 신뢰할 수 있는 파트너로서 데이터센터, 반도체 제조업, 자동차 산업 등 필요한 곳 어디든 고성능 반도체를 위한 맞춤형 냉각 솔루션을 제공합니다.
                    </span>`
                )}
                linkText={glv(
                  "View our Full Product Range",
                  "전체 제품 목록 확인하기"
                )}
                link="/product"
              />
            </section>
            <section
              name="4"
              className="w-full h-full overflow-hidden"
              style={{ scrollSnapAlign: "start" }}
            >
              <Content
                title={glv("Patent-Driven Technology.", "글로벌 특허 기술.")}
                img={
                  <>
                    <div className="w-full h-full">
                      <div className="md:w-1/2 w-full h-full flex justify-center items-center overflow-hidden">
                        <img
                          src={require("../assets/chip2.png")}
                          className="object-cover w-full"
                          alt=""
                        />
                      </div>
                    </div>
                  </>
                }
                content={glv(
                  "KoolMicro’s IMMC technology powers advanced semiconductor cooling by optimizing internal structures for efficient heat transfer. Unlike traditional methods, it leverages innovative fluid dynamics for direct, superior thermal management.<br/><br/>With a Korean-granted patent and multiple U.S. applications in progress, our intellectual property portfolio safeguards the uniqueness and performance of our solutions on a global scale.",
                  "<span class='text-[#5F5D59]'>IMMC 기술은 내부 구조를 최적화하여 효율적인 열 전달을 구현함으로써 첨단 반도체 냉각을 가능하게 합니다.<br/>기존 방식과 차별화된 혁신적인 방식으로 유체 역학을 활용해 직접적이고 우수한 열 관리 성능을 제공합니다.<br/><br/>국내에서 특허를 취득했으며, 다수의 미국 특허도 진행중인 상황으로 IMMC 기술의 독창성과 성능은 국내뿐 아니라 글로벌 차원에서 보호받고 있습니다.</span>"
                )}
                linkText="IMMC 기술 자세히 알아보기"
                link="/technology"
              />
            </section>
          </>
        ) : (
          <>
            <section name="3" className="w-full" />
            <section
              name="4"
              className="flex flex-row w-full h-full overflow-hidden bg-brand-silicon-white"
              style={{ scrollSnapAlign: "start" }}
            >
              <div className="flex font-pretendard w-1/2 h-full ">
                <div className="flex justify-start items-center h-full">
                  <div className="h-full lg:w-[10rem] w-[5rem] grid grid-cols-1 gap-1 py-[5rem]">
                    <div className="w-full flex items-center overflow-y-hidden">
                      <img
                        src={require("../assets/cover/cpu.jpg")}
                        className="w-full h-full object-fit"
                        alt=""
                      />
                    </div>
                    <div className="w-full flex items-center overflow-y-hidden">
                      <img
                        src={require("../assets/cover/gpu.jpg")}
                        className="w-full h-full object-fit"
                        alt=""
                      />
                    </div>
                    <div className="w-full h-full flex items-center overflow-y-hidden">
                      <img
                        src={require("../assets/cover/server.jpg")}
                        className="w-full h-full object-fit"
                        alt=""
                      />
                    </div>
                    <div className="w-full flex items-center overflow-y-hidden">
                      <img
                        src={require("../assets/cover/datacenter.jpg")}
                        className="w-full h-full object-fit"
                        alt=""
                      />
                    </div>
                    <div className="w-full flex items-center overflow-y-hidden">
                      <img
                        src={require("../assets/cover/automotive.jpg")}
                        className="w-full h-full object-fit"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col justify-center h-full items-center px-5 text-center w-[30rem] mx-auto">
                  <div className="font-black f-28 md:f-28 md:mb-10 mb-5">
                    {glv(
                      "Anywhere, Everywhere.",
                      "어느곳 어디든, 한계없는 냉각."
                    )}
                  </div>
                  <div className="font-[300] md:f-20 f-10">
                    {glv(
                      <span>
                        IMMC's unique manifold microchannel structure, chip
                        integration method, and flow control method enables
                        uniform cooling of a larger area with lower pumping
                        power at a reasonable price.
                        <br />
                        <br />
                        We offer innovative Direct-to-Chip liquid cooling
                        solutions for AI and high-performance semiconductor
                        chips, optimizing performance and dramatically enhancing
                        energy efficiency for data centers, semiconductor
                        manufacturers, and the automotive industry as a global
                        leader and trusted partner.
                      </span>,
                      <span class="text-[#5F5D59]">
                        IMMC 기술의 독창적인 구조, 칩 집적 방식, 그리고 유동
                        제어 기술은 합리적인 비용과 낮은 펌핑 전력으로 더 넓은
                        영역을 균일하게 냉각할 수 있게 합니다.
                        <br />
                        <br />
                        인공지능 및 고성능 반도체 칩을 위한 혁신적인
                        Direct-to-Chip 수냉 솔루션을 제공하며, 성능을 최적화하고
                        에너지효율을 획기적으로 향상시킵니다.
                        <br />
                        <br />
                        글로벌 리더이자 신뢰할 수 있는 파트너로서 데이터센터,
                        반도체 제조업, 자동차 산업 등 필요한 곳 어디든 고성능
                        반도체를 위한 맞춤형 냉각 솔루션을 제공합니다.
                      </span>
                    )}
                  </div>
                  <div className="mt-10 font-extrabold">
                    <Link to="/product">
                      <span className="underline">
                        {glv(
                          "View our Full Product Range",
                          "전체 제품 목록 확인하기"
                        )}
                      </span>{" "}
                      ➜
                    </Link>
                  </div>
                </div>
              </div>
              <div className="w-[1px] my-auto bg-brand-steel-grey h-[50%]"></div>
              <div className="flex font-pretendard w-1/2 h-full ">
                <div className="flex flex-col justify-center h-full items-center px-5 text-center w-[30rem] mx-auto">
                  <div className="font-black f-28 md:f-28 md:mb-10 mb-5">
                    {glv("Patent-Driven Technology.", "글로벌 특허 기술.")}
                  </div>
                  <div className="font-[300] md:f-20 f-10">
                    {glv(
                      <span>
                        KoolMicro's IMMC technology powers advanced
                        semiconductor cooling by optimizing internal structures
                        for efficient heat transfer. Unlike traditional methods,
                        it leverages innovative fluid dynamics for direct,
                        superior thermal management.
                        <br />
                        <br />
                        With a Korean-granted patent and multiple U.S.
                        applications in progress, our intellectual property
                        portfolio safeguards the uniqueness and performance of
                        our solutions on a global scale.
                      </span>,
                      <span className="font-light text-[#5F5D59]">
                        IMMC 기술은 내부 구조를 최적화하여 효율적인 열 전달을
                        구현함으로써 첨단 반도체 냉각을 가능하게 합니다.
                        <br />
                        기존 방식과 차별화된 혁신적인 방식으로 유체 역학을
                        활용해 직접적이고 우수한 열 관리 성능을 제공합니다.
                        <br />
                        <br />
                        국내에서 특허를 취득했으며, 다수의 미국 특허도 진행중인
                        상황으로 IMMC 기술의 독창성과 성능은 국내뿐 아니라
                        글로벌 차원에서 보호받고 있습니다.
                      </span>
                    )}
                  </div>
                  <div className="mt-10 font-extrabold">
                    <Link to="/technology">
                      <span className="underline">
                        {glv(
                          "Learn more about our Tech",
                          "IMMC 기술 자세히 알아보기"
                        )}
                      </span>{" "}
                      ➜
                    </Link>
                  </div>
                </div>
                <div className="flex justify-start items-center h-full">
                  <div className="lg:w-[10rem] w-[5rem] flex items-center h-full py-[5rem]">
                    <img
                      src={require("../assets/chip2.png")}
                      className="object-fit rotate-180 h-full w-full"
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </section>
          </>
        )} */}
        <section
          name="5"
          className="flex w-full h-full md:overflow-y-auto overflow-y-scroll"
          style={{ scrollSnapAlign: "start" }}
        >
          <ContactSection />
        </section>
      </div>
    </>
  );
}

export default MainPage;
